import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Dialog, FormControlLabel, Typography } from '@material-ui/core';
import { useLocation } from 'react-router-dom';

import { CustomButton } from '../button';
import CustomCheckbox from '../button/CustomCheckbox';
import useNotice, { todayKey } from './useNotice';

// 공지 팝업 — 첫 방문 때 한 번. '오늘 하루 보지 않기'는 이 브라우저에만 저장된다
const hideKey = (key) => 'qw_notice_hide:' + key;

const useStyles = makeStyles((theme) => ({
  paper: {
    width: 460,
    maxWidth: 'calc(100% - 32px)',
    margin: 16,
    padding: '36px 32px 28px',
    borderRadius: 20,
    boxShadow: '0 0 7px rgba(0, 0, 0, 0.15)',
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: { padding: '32px 22px 24px' },
  },
  badge: {
    display: 'inline-block',
    alignSelf: 'flex-start',
    height: 26,
    lineHeight: '26px',
    padding: '0 10px',
    borderRadius: 13,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: -0.6,
    color: theme.palette.primary.dark,
    backgroundColor: '#fef7f5',
    marginBottom: 14,
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: -1.3,
    color: '#161617',
    marginBottom: 14,
    wordBreak: 'keep-all',
    [theme.breakpoints.down('sm')]: { fontSize: 22 },
  },
  line: {
    fontSize: 16,
    lineHeight: 1.7,
    letterSpacing: -0.8,
    color: '#555558',
    wordBreak: 'keep-all',
  },
  schedule: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 18,
    padding: '12px 14px',
    borderRadius: 10,
    backgroundColor: '#fef7f5',
    color: theme.palette.primary.dark,
    fontSize: 15,
    fontWeight: 700,
    letterSpacing: -0.7,
    '& svg': { flex: 'none', marginRight: 8 },
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 24,
    '& .MuiFormControlLabel-root': { marginLeft: -6 },
    '& .MuiButton-root': { width: 120, height: 44, fontSize: 16 },
  },
  hideLabel: {
    fontSize: 14,
    color: '#a4a4ac',
    letterSpacing: -0.6,
  },
}));

function NoticeModal() {
  const cs = useStyles();
  const notice = useNotice();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [hideToday, setHideToday] = useState(false);

  useEffect(() => {
    if (!notice || !notice.showModal) return;
    try {
      if (localStorage.getItem(hideKey(notice.key)) === todayKey()) return;
    } catch (e) { /* 저장 공간이 막힌 브라우저는 항상 보여준다 */ }
    setOpen(true);
  }, [notice]);

  const close = () => {
    if (hideToday && notice) {
      try { localStorage.setItem(hideKey(notice.key), todayKey()); } catch (e) { /* 무시 */ }
    }
    setOpen(false);
  };

  if (!notice || pathname.startsWith('/admin') || pathname.startsWith('/t/')) return null;   // 관리자·고객 배송조회 화면에는 공지 모달을 띄우지 않는다

  return (
    <Dialog open={open} onClose={close} maxWidth={false} classes={{ paper: cs.paper }} aria-labelledby="noticeTitle">
      {notice.badge && <span className={cs.badge}>{notice.badge}</span>}
      <Typography id="noticeTitle" component="h2" className={cs.title}>{notice.title}</Typography>
      {(notice.lines || []).map((line, i) => (
        <Typography key={i} className={cs.line}>{line}</Typography>
      ))}
      {notice.schedule && (
        <div className={cs.schedule}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3.5" y="5" width="17" height="15.5" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4" />
          </svg>
          {notice.schedule}
        </div>
      )}
      <div className={cs.footer}>
        <FormControlLabel
          control={<CustomCheckbox checked={hideToday} onChange={(e, checked) => setHideToday(Boolean(checked))} />}
          label={<span className={cs.hideLabel}>오늘 하루 보지 않기</span>}
        />
        <CustomButton variant="contained" color="primary" onClick={close}>확인</CustomButton>
      </div>
    </Dialog>
  );
}

export default NoticeModal;
